import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { redirect } from 'react-router';
import {
  POST_SIGNOUT_URL,
  PROFILE_2FA_URL,
  SIGNIN_2FA_URL,
  SIGNIN_RECOVERY_URL,
  SIGNIN_RESET_PASSWORD_URL,
  SIGNIN_URL,
  SIGNOUT_URL,
  SIGNUP_2FA_URL,
  SIGNUP_CONFIRMATION_URL,
  SIGNUP_URL,
  authPages,
} from '@skynet/auth-feature';
import { loansArchiveConfig } from '@skynet/loans-feature';
import { retainersArchiveConfig } from '@skynet/retainers-feature';
import { botCommandsConfig } from '@skynet/admin-bot-commands-feature';
import { botMessagesConfig } from '@skynet/bot-messages-feature';
import { smmConfig } from '@skynet/smm-feature';
import { tradingGroupsConfig } from '@skynet/trading-groups-feature';
import { dexExchangesConfig } from '@skynet/dex-exchanges-feature';
import { exchangesConfig } from '@skynet/exchanges-feature';
import { usersConfig } from '@skynet/users-feature';
import { regionsConfig } from '@skynet/regions-feature';
import { itemsConfig } from '@skynet/items-feature';
import { profileFeatureRoute } from '@skynet/profile-feature';
import { tenantsConfig } from '@skynet/tenants-feature';
import { rolesConfig } from '@skynet/roles-feature';
import { arbitrageConfig } from '@skynet/arbitrage-feature';
import {
  SERVERS_ACCOUNT_ROUTE_PATH,
  SERVERS_SERVER_ROUTE_PATH,
  serversConfig,
} from '@skynet/servers-feature';
import { marketingConfig } from '@skynet/marketing-feature';
import { tokenMappingConfig } from '@skynet/token-mapping-feature';
import { RedirectDefaults } from 'src/navigation/RedirectDefaults';
import { reloadOnUpdate } from 'src/utils/routing/reload-on-update';

const withRedirectDefaults = <T extends object>(ImportedComponent: ComponentType<T>) => {
  return function WrappedComponent(props: T) {
    return (
      <RedirectDefaults>
        <ImportedComponent {...props} />
      </RedirectDefaults>
    );
  };
};

/**
 * Lazy `Component` of a feature config's page, reloading the app first if a new
 * version was deployed. A section that takes an entity id (the default) is
 * wrapped in `RedirectDefaults`, which fills a missing id from the selected
 * entity; `needsEntityId: false` pages render as they are.
 */
const featurePage =
  (config: {
    route: { load: () => Promise<{ default: ComponentType }> };
    needsEntityId?: boolean;
  }) =>
  async () => {
    const Component = (await reloadOnUpdate(config.route.load)()).default;
    return config.needsEntityId === false ? Component : withRedirectDefaults(Component);
  };

/** Lazy `Component` of an auth page, reloading the app first if a new version was deployed. */
const authPage = (load: (typeof authPages)[keyof typeof authPages]) => async () =>
  (await reloadOnUpdate(load)()).default;

// The auth paths are the feature's absolute URLs (nested absolute paths extend
// their parent's), so the route table cannot drift from the URLs the pages use.
export const authRoutes: RouteObject[] = [
  {
    path: SIGNUP_URL,
    handle: { skipAuthorization: true },
    lazy: { Component: authPage(authPages.signUpLayout) },
    children: [
      {
        index: true,
        path: ':referralId',
        lazy: { Component: authPage(authPages.signUp) },
      },
      {
        path: SIGNUP_CONFIRMATION_URL,
        lazy: { Component: authPage(authPages.signUpConfirmation) },
        children: [
          {
            path: `${SIGNUP_CONFIRMATION_URL}/:token`,
            lazy: { Component: authPage(authPages.signUpConfirmation) },
          },
        ],
      },
      {
        path: SIGNUP_2FA_URL,
        lazy: { Component: authPage(authPages.signUp2FA) },
      },
    ],
  },
  {
    path: SIGNIN_URL,
    handle: { skipAuthorization: true },
    lazy: {
      Component: authPage(authPages.signInLayout),
      ErrorBoundary: async () =>
        (await reloadOnUpdate(async () => import('src/pages/Error'))()).default,
    },
    children: [
      {
        path: '',
        index: true,
        lazy: { Component: authPage(authPages.signIn) },
      },
      {
        path: SIGNIN_2FA_URL,
        lazy: { Component: authPage(authPages.signIn2FA) },
      },
      {
        // The splat only catches pre-SKY-4829 `/reset-password/:token` links,
        // which the page drops from the URL before asking for a new code.
        path: `${SIGNIN_RESET_PASSWORD_URL}/*`,
        lazy: { Component: authPage(authPages.signInResetPassword) },
      },
      {
        path: SIGNIN_RECOVERY_URL,
        lazy: { Component: authPage(authPages.signInRecovery) },
      },
    ],
  },
  {
    path: SIGNOUT_URL,
    handle: { skipAuthorization: true },
    lazy: {
      // HydrateFallback: async () => (await import('src/navigation/BlankLoading')).BlankLoading,
      loader: async () => (await import('src/pages/Signout/loader')).signoutLoader,
    },
  },
  {
    path: POST_SIGNOUT_URL,
    handle: { skipAuthorization: true },
    lazy: {
      // HydrateFallback: async () => (await import('src/navigation/BlankLoading')).BlankLoading,
      loader: async () => (await import('src/pages/Signout/loader')).postSignoutLoader,
    },
  },
  {
    path: '/switch-tenant/:tenantId/:tenantSlug',
    handle: { skipAuthorization: true },
    lazy: {
      loader: async () => (await import('src/pages/SwitchTenant/loader')).switchTenantLoader,
    },
  },
  {
    path: '/login',
    loader: () => redirect(SIGNIN_URL),
    handle: { skipAuthorization: true },
  },
  {
    path: '/logout',
    loader: () => redirect(SIGNOUT_URL),
    handle: { skipAuthorization: true },
  },
  //alias for signup/2fa
  {
    path: PROFILE_2FA_URL,
    handle: { skipAuthorization: true },
    lazy: { Component: authPage(authPages.signUpLayout) },
    children: [
      {
        index: true,
        handle: { skipAuthorization: true },
        lazy: { Component: authPage(authPages.signUp2FA) },
      },
    ],
  },
];

export const loansRoutes: RouteObject = {
  path: 'loans?',
  lazy: {
    loader: async () => (await import('src/routes/loaders/domain-data-loader')).loaderDomainData,
    Component: async () => (await reloadOnUpdate(async () => import('src/pages/Loans'))()).default,
  },
  children: [
    {
      path: 'summary',
      index: true,
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/Loans/Summary'))()).default,
      },
    },
    {
      path: 'overview/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (await reloadOnUpdate(async () => import('src/pages/Loans/Overview'))())
            .default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'terms/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Loans/LoanTerms'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'balances/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (await reloadOnUpdate(async () => import('src/pages/Loans/Balances'))())
            .default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'tasks/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (await reloadOnUpdate(async () => import('src/pages/Loans/Tasks'))())
            .default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'transactions/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Loans/Transactions'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      // path: 'bots/:loanId?',
      path: 'bots/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (await reloadOnUpdate(async () => import('src/pages/Loans/Trading'))())
            .default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'arbitrage-info/:loanId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/ArbitrageInfo'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'not-found',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/Loans/NotFound'))()).default,
      },
    },
    {
      path: loansArchiveConfig.route.path,
      lazy: { Component: featurePage(loansArchiveConfig) },
    },
  ],
};

export const retainersRoutes: RouteObject = {
  path: 'retainers',
  lazy: {
    loader: async () => (await import('src/routes/loaders/domain-data-loader')).loaderDomainData,
    Component: async () =>
      (await reloadOnUpdate(async () => import('src/pages/Retainers'))()).default,
  },
  children: [
    {
      path: 'overview-admin/:projectId?',
      index: true,
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/Overview'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'overview/:projectId?',
      index: true,
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/ClientView/Overview'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'terms/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/ClientView/Terms'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'terms-admin/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/Terms'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'tasks-admin/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/RetainersTasks'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'tasks/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/ClientView/Tasks'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'balances-admin/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/Balances'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'balances/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/ClientView/Balances'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'not-found',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/Retainers/NotFound'))()).default,
      },
    },
    {
      // path: 'bots/:projectId?',
      path: 'bots/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/Trading'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'arbitrage-info/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/ArbitrageInfo'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'transactions/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Retainers/Transactions'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'clients/:projectId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(
              async () => import('src/pages/Retainers/RetainersClientManagement'),
            )()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: retainersArchiveConfig.route.path,
      lazy: { Component: featurePage(retainersArchiveConfig) },
    },
  ],
};

/**
 * The Admin domain's route. Its `path` is the segment every admin feature's
 * literal `nav.url` starts with; `admin-feature-urls.test.ts` keeps the two in
 * agreement.
 */
export const adminRoutes: RouteObject = {
  path: 'admin',
  lazy: {
    loader: async () => (await import('src/routes/loaders/domain-data-loader')).loaderDomainData,
    Component: async () => {
      const Component = (await reloadOnUpdate(async () => import('src/pages/Admin'))()).default;
      return withRedirectDefaults(Component);
    },
  },
  children: [
    {
      path: itemsConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(itemsConfig.route.load)()).default,
      },
    },
    {
      path: itemsConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(itemsConfig.detailRoute.load)()).default,
      },
    },
    {
      // The packaged page plus the app-owned slots it renders (account list,
      // account details and transactions), loaded together on route entry. The
      // server and account params are element-less children, so moving between
      // them keeps the page mounted.
      path: serversConfig.route.path,
      lazy: {
        Component: async () => {
          // `featurePage` runs the reload-on-update check for the route.
          const [Page, { withServersSlots }] = await Promise.all([
            featurePage(serversConfig)(),
            import('src/pages/Admin/ServersRoute'),
          ]);
          return withServersSlots(Page);
        },
      },
      children: [
        {
          path: SERVERS_SERVER_ROUTE_PATH,
          children: [{ path: SERVERS_ACCOUNT_ROUTE_PATH }],
        },
      ],
    },
    {
      // Route, path and lazy loader come from the feature package's config, so
      // the page implementation stays code-split (loaded on route entry) with no
      // intermediate re-export shim in the app.
      path: arbitrageConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(arbitrageConfig.route.load)()).default,
      },
    },
    {
      path: 'sub-accounts',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/Admin/SubAccounts'))()).default,
      },
    },
    {
      path: 'log',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/Admin/Log'))()).default,
      },
    },
    {
      // Route, path and lazy loader come from the feature package's config, so
      // the page implementation stays code-split (loaded on route entry) with no
      // intermediate re-export shim in the app.
      path: marketingConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(marketingConfig.route.load)()).default,
      },
    },
    {
      path: marketingConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(marketingConfig.detailRoute.load)()).default,
      },
    },
    {
      // Route, path and lazy loader come from the feature package's config, so
      // the page implementation stays code-split (loaded on route entry) with no
      // intermediate re-export shim in the app.
      path: botCommandsConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(botCommandsConfig.route.load)()).default,
      },
    },
    {
      // Detail route, path and lazy loader also come from the feature package.
      path: botCommandsConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(botCommandsConfig.detailRoute.load)()).default,
      },
    },
    {
      // Route, path and lazy loader come from the feature package's config, so
      // the page implementation stays code-split (loaded on route entry) with no
      // intermediate re-export shim in the app.
      path: botMessagesConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(botMessagesConfig.route.load)()).default,
      },
    },
    {
      path: tenantsConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(tenantsConfig.route.load)()).default,
      },
    },
    {
      // Detail route, path and lazy loader also come from the feature package.
      path: tenantsConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(tenantsConfig.detailRoute.load)()).default,
      },
    },
    {
      path: usersConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(usersConfig.route.load)()).default,
      },
    },
    {
      path: usersConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(usersConfig.detailRoute.load)()).default,
      },
    },
    {
      path: rolesConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(rolesConfig.route.load)()).default,
      },
    },
    {
      path: rolesConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(rolesConfig.detailRoute.load)()).default,
      },
    },
    {
      path: exchangesConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(exchangesConfig.route.load)()).default,
      },
    },
    {
      path: exchangesConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(exchangesConfig.detailRoute.load)()).default,
      },
    },
    {
      path: regionsConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(regionsConfig.route.load)()).default,
      },
    },
    {
      path: regionsConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(regionsConfig.detailRoute.load)()).default,
      },
    },
    {
      path: tokenMappingConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(tokenMappingConfig.route.load)()).default,
      },
    },
    {
      path: tradingGroupsConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(tradingGroupsConfig.route.load)()).default,
      },
    },
    {
      path: tradingGroupsConfig.detailRoute.path,
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(tradingGroupsConfig.detailRoute.load)()).default,
      },
    },
    {
      // Both SMM routes (posts page and single post) and their lazy loaders
      // come from the feature package, so the pages stay code-split. There is
      // no `/new` route: "New post" is a composer modal on the posts page.
      path: smmConfig.route.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(smmConfig.route.load)()).default,
      },
    },
    {
      path: smmConfig.detailRoute.path,
      lazy: {
        Component: async () => (await reloadOnUpdate(smmConfig.detailRoute.load)()).default,
      },
    },
  ],
};

export const dexRoutes: RouteObject = {
  path: 'dex',
  lazy: {
    loader: async () => (await import('src/routes/loaders/domain-data-loader')).loaderDomainData,
    Component: async () => (await reloadOnUpdate(async () => import('src/pages/DEX'))()).default,
  },
  children: [
    {
      path: 'blockchains',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/DEX/Blockchains'))()).default,
      },
    },
    {
      path: 'blockchains/:blockchainId',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/DEX/BlockchainPage'))()).default,
      },
    },
    {
      path: dexExchangesConfig.route.path,
      lazy: { Component: featurePage(dexExchangesConfig) },
    },
    {
      path: 'transactions',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/DEX/Transactions/index'))()).default,
      },
    },
    {
      path: 'vaults',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/DEX/Vaults'))()).default,
      },
    },
    {
      path: 'vault-overview/:id?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/DEX/VaultOverview'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'vault-tasks/:id?',
      lazy: {
        Component: async () => {
          const Component = (await reloadOnUpdate(async () => import('src/pages/DEX/Tasks'))())
            .default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'tasks',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/DEX/Tasks'))()).default,
      },
    },
    {
      path: 'vault-details/:id?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/DEX/VaultDetails'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'vault-transactions/:id?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/DEX/Transactions/index'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'not-found',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/DEX/NotFound'))()).default,
      },
    },
  ],
};

export const tradingRoutes = {
  path: 'trading',
  lazy: {
    loader: async () => (await import('src/routes/loaders/domain-data-loader')).loaderDomainData,
    Component: async () =>
      (await reloadOnUpdate(async () => import('src/pages/Trading'))()).default,
  },
  children: [
    {
      path: 'bots/:exchangeId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Trading/Bots/index'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'tasks/new',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(
              async () => import('src/pages/Trading/TradingApiKeys/TradingApiKeys'),
            )()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'tasks/:accountId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(
              async () => import('src/pages/Trading/TradingApiKeys/TradingTasksPage'),
            )()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'reports/:traderId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Trading/Reports'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'dex-setup',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(async () => import('src/pages/Trading/Dex/index'))()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'balances/:traderId?',
      lazy: {
        Component: async () => {
          const Component = (
            await reloadOnUpdate(
              async () => import('src/pages/Trading/Balances/TradingBalancePage'),
            )()
          ).default;
          return withRedirectDefaults(Component);
        },
      },
    },
    {
      path: 'not-found',
      lazy: {
        Component: async () =>
          (await reloadOnUpdate(async () => import('src/pages/Trading/NotFound'))()).default,
      },
    },
  ],
};

export const profileRoute = {
  path: profileFeatureRoute.path,
  handle: profileFeatureRoute.handle,
  lazy: {
    loader: async () => (await import('src/routes/loaders/domain-data-loader')).loaderDomainData,
    Component: async () =>
      (await reloadOnUpdate(async () => import('src/pages/Profile'))()).default,
  },
  children: [
    {
      index: true,
      lazy: {
        Component: async () => (await reloadOnUpdate(profileFeatureRoute.load)()).default,
      },
    },
  ],
};

export const publicRoutes = [
  {
    path: 'cookies',
    handle: { skipAuthorization: true },
    lazy: {
      Component: async () =>
        (await reloadOnUpdate(async () => import('src/pages/CookiesPage'))()).default,
    },
  },
  {
    path: 'terms',
    handle: { skipAuthorization: true },
    lazy: {
      Component: async () =>
        (await reloadOnUpdate(async () => import('src/pages/TermsPage'))()).default,
    },
  },
];

/**
 * Retired URLs, redirected to the pages that replaced them. Top level, outside
 * the root route on purpose: its loader sends a section missing from the
 * allowed navigation actions to not-found before any child loader could run,
 * and a retired page's action (e.g. `read:admin:keys`) is gone from Cerbos.
 */
export const legacyRedirectRoutes: RouteObject[] = [
  {
    // The API keys page, replaced by sub-accounts (SKY-3540). The query is
    // kept: `search` means the same on both pages.
    path: '/admin/keys',
    handle: { skipAuthorization: true },
    loader: ({ request }) => redirect(`/admin/sub-accounts${new URL(request.url).search}`),
  },
];

export const exceptionRoutes = [
  // TODO: do we still need this page if we have "*" path handler below?
  {
    path: 'not-found',
    index: true,
    handle: { skipAuthorization: true },
    lazy: {
      Component: async () =>
        (await reloadOnUpdate(async () => import('src/pages/not-found'))()).default,
    },
  },
  {
    path: 'not-connected',
    index: true,
    handle: { skipAuthorization: true },
    lazy: {
      Component: async () =>
        (await reloadOnUpdate(async () => import('src/pages/not-connected'))()).default,
    },
  },
  {
    path: '*',
    handle: { skipAuthorization: true },
    lazy: {
      Component: async () =>
        (await reloadOnUpdate(async () => import('src/pages/not-found'))()).default,
    },
  },
];
